// src/pages/profile-settings/doctor/PracticeTeamInviteAccept.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction.js";
import Button from "@/components/ui/Button.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function readInviteParams(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    return {
      email: String(params.get("email") || "").trim().toLowerCase(),
      token: String(params.get("token") || "").trim(),
    };
  } catch {
    return { email: "", token: "" };
  }
}

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

async function acceptPracticeTeamInvite({ token, email }) {
  const accessToken = await resolveFunctionAccessToken({
    refreshIfMissing: true,
    forceRefresh: false,
  });
  if (!String(accessToken || "").trim()) {
    throw new Error("auth_required");
  }

  return callFunctionRaw(
    "practice-team-invite-lifecycle",
    {
      action: "accept",
      token,
      email,
    },
    {
      accessToken,
      timeoutMs: 12000,
      preferFreshSession: true,
    },
  );
}

export default function PracticeTeamInviteAccept() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const invite = React.useMemo(() => readInviteParams(location.search), [location.search]);
  const [state, setState] = React.useState("loading");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function runAccept() {
      setState("loading");
      setError("");
      try {
        if (!invite.token || !invite.email) {
          throw new Error("missing_invite");
        }
        const result = await acceptPracticeTeamInvite(invite);
        if (!alive) return;
        const nextRoute = String(result?.next_route || "").trim();
        navigate(nextRoute || `/practice-team/welcome?member=${encodeURIComponent(result?.invite?.id || "")}`, {
          replace: true,
        });
      } catch (nextError) {
        if (!alive) return;
        setState("error");
        const message = String(nextError?.body?.error || nextError?.message || "").trim();
        if (message === "email_mismatch") {
          setError(
            t(
              "settings.practiceTeam.accept.emailMismatch",
              isFrench
                ? "Cette invitation doit être acceptée avec l’adresse e-mail invitée."
                : "This invitation must be accepted with the invited email address."
            )
          );
        } else if (message === "invite_suspended") {
          setError(
            t(
              "settings.practiceTeam.accept.suspended",
              isFrench
                ? "Cette invitation à l’équipe de pratique est actuellement suspendue."
                : "This Practice Team invitation is currently suspended."
            )
          );
        } else {
          setError(
            t(
              "settings.practiceTeam.accept.error",
              isFrench
                ? "Nous n’avons pas pu accepter cette invitation à l’équipe de pratique pour le moment."
                : "We could not accept this Practice Team invitation right now."
            )
          );
        }
      }
    }

    runAccept();

    return () => {
      alive = false;
    };
  }, [invite, navigate, t]);

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <main className="afrimed-rail afrimed-rail--comfortable pt-3 pb-12">
        <TopBar
          title={t("settings.practiceTeam.accept.title", isFrench ? "Accepter l’invitation à l’équipe de pratique" : "Accept Practice Team invitation")}
          subtitle={t(
            "settings.practiceTeam.accept.subtitle",
            isFrench
              ? "AfriMed™ prépare votre accès limité à l’équipe de pratique."
              : "AfriMed™ is preparing your limited Practice Team access."
          )}
          onBack={() => navigate("/login")}
          titleWrapperClassName="mt-2 mb-5"
        />

        <div className="w-full max-w-[1120px] space-y-5">
          {state === "loading" ? (
            <p className="text-body-medium text-on-surface-var">
              {t("settings.practiceTeam.accept.loading", isFrench ? "Acceptation de l’invitation..." : "Accepting invitation...")}
            </p>
          ) : null}

          {error ? (
            <>
              <p className="text-body-medium text-[rgb(var(--afrimed-danger))]">{error}</p>
              <Button type="button" onClick={() => navigate("/login?sheet=1")}>
                {t("settings.practiceTeam.accept.loginCta", isFrench ? "Retour à la connexion" : "Return to login")}
              </Button>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
